import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import MenuButton from './MenuButton';
import MenuContent from './MenuContent';
import { useAuth } from '../auth/AuthContext';
import { getNameInitials, getPrimaryDisplayRole } from '../auth/authUtils';

interface SideMenuMobileProps {
  open: boolean | undefined;
  toggleDrawer: (newOpen: boolean) => () => void;
}

export default function SideMenuMobile({ open, toggleDrawer }: SideMenuMobileProps) {
  const { user, realmRoles, logout } = useAuth();
  const displayName = user?.name ?? user?.email ?? 'User';
  const displayRole = getPrimaryDisplayRole(realmRoles);
  const email =
    user?.email && user.name !== user.email ? user.email : undefined;
  const initials = getNameInitials(displayName);

  const handleLogout = () => {
    toggleDrawer(false)();
    logout();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: 'none',
          background: 'linear-gradient(180deg, #5136F2 0%, #100C2E 100%)',
          color: '#F5F5FA',
        },
      }}
    >
      <Stack
        sx={{
          maxWidth: '70dvw',
          height: '100%',
        }}
      >
        <Stack direction="row" sx={{ p: 2, pb: 0, gap: 1, alignItems: 'flex-start' }}>
          <Stack
            direction="row"
            sx={{ gap: 1.25, alignItems: 'flex-start', flexGrow: 1, minWidth: 0, p: 1 }}
          >
            <Avatar
              alt={displayName}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.12)',
                color: '#F5F5FA',
              }}
            >
              {initials}
            </Avatar>
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1, py: 0.25 }}>
              <Typography
                component="p"
                variant="subtitle1"
                title={displayName}
                sx={{ fontWeight: 600, lineHeight: 1.35, color: '#F5F5FA' }}
              >
                {displayName}
              </Typography>
              {email && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    lineHeight: 1.45,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  {email}
                </Typography>
              )}
              {displayRole && (
                <Box sx={{ pt: 0.25 }}>
                  <Chip
                    size="small"
                    label={displayRole}
                    variant="outlined"
                    sx={{
                      height: 22,
                      maxWidth: '100%',
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.75)',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
          <MenuButton
            showBadge
            sx={{
              flexShrink: 0,
              mt: 0.5,
              color: '#F5F5FA',
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.14)',
                borderColor: 'rgba(255,255,255,0.4)',
              },
            }}
          >
            <NotificationsRoundedIcon />
          </MenuButton>
        </Stack>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <Stack sx={{ flexGrow: 1 }}>
          <MenuContent onNavigate={toggleDrawer(false)} />
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        </Stack>
        <Stack sx={{ p: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LogoutRoundedIcon />}
            onClick={handleLogout}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              color: '#F5F5FA',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': {
                borderColor: '#F5F5FA',
                backgroundColor: 'rgba(255,255,255,0.16)',
              },
            }}
          >
            Logout
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
